import { useLazyQuery } from "@apollo/client";
import { FETCH_ORDER_FOR_MAP } from "api/queries/Queries";
import { stagingOptions } from "constants/apollo";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { backgroundColors, routeBgColors, textColors } from "../../../components/colors";
import { getStatusColor } from "helper/getStatusColor";
import { EyeIcon, EyeSlashIcon } from "components/svg";
import { shipmentTypes } from "constants/enums";

const OrderCard = ({ data, color = "success", onClick = () => { }, selectedShipmentIds, selectedShipments, selectedRef, inSelectedCards = false }) => {
  const { t } = useTranslation("shipments");
  const [isOpen, setIsOpen] = useState(false);
  const [shipmentData, setShipmentData] = useState(data);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const wasSelectedRef = useRef(false);
  const [getShipment, { data: fetchedShipment, loading, error: shipmentsFetchError }] = useLazyQuery(FETCH_ORDER_FOR_MAP, stagingOptions);
  // identify shipment status
  let statusColor = getStatusColor(data?.orderStatus); // default-unassigned, success-completed, danger-cancelled, warning-assigned
  let statusText = (data?.orderStatus || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  useEffect(() => {
    if (!selectedShipmentIds || data?.id == null) return;
    const selected = selectedShipmentIds.some((sid) => String(sid) === String(data.id));
    if (selected) {
      if (!wasSelectedRef.current) {
        setIsOpen(true);
        getShipment({
          variables: {
            id: parseInt(data?.id, 10),
          },
        });
      }
      wasSelectedRef.current = true;
    } else {
      wasSelectedRef.current = false;
      setIsOpen(false);
    }
  }, [selectedShipmentIds, data?.id, getShipment]);
  useEffect(() => {
    if (fetchedShipment) {
      setShipmentData(fetchedShipment?.fetchOrder);
    }
  }, [fetchedShipment]);

  const isBarq = data?.shipmentType === "barq";
  const orderStatusForTimer = (shipmentData?.orderStatus ?? data?.orderStatus) || "";
  const isCompletedOrCancelled = orderStatusForTimer === "completed" || orderStatusForTimer === "cancelled";
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const promisedAtMs = useMemo(() => {
    const v = shipmentData?.promisedTime ?? data?.promisedTime;
    if (!v) return null;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : null;
  }, [shipmentData?.promisedTime, data?.promisedTime]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!isBarq || !promisedAtMs || isCompletedOrCancelled) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isBarq, promisedAtMs, isCompletedOrCancelled]);

  const isOverdue = isBarq && promisedAtMs != null && nowMs > promisedAtMs && !isCompletedOrCancelled;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const timeLeftText = useMemo(() => {
    if (!isBarq || !promisedAtMs || isCompletedOrCancelled) return null;
    const diffMs = promisedAtMs - nowMs;
    const abs = Math.abs(diffMs);
    const totalSeconds = Math.floor(abs / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad2 = (n) => String(n).padStart(2, "0");
    const body = h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
    // Green badge: time left. Red badge: total overdue time.
    return diffMs < 0 ? body : body;
  }, [isBarq, promisedAtMs, nowMs, isCompletedOrCancelled]);

  const isSelected = selectedShipmentIds?.some((sid) => String(sid) === String(data?.id));
  const shouldRenderCard = (inSelectedCards && isSelected) || (!isSelected && !inSelectedCards);
  if (!shouldRenderCard) return null;

  const slaColor = shipmentTypes.find((item) => item.value === data?.shipmentType);
  const sla = (data?.shipmentType || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
      <div
        ref={selectedShipmentIds[selectedShipmentIds.length - 1] == data.id ? selectedRef : null}
        className={`select-none max-w-[383px] rounded-xl ${isSelected ? "bg-lightBlue" : isBarq ? "bg-orange-50 animate-barq-pulse" : "bg-white hover:bg-lightGray"} text-xs hover:cursor-pointer transition-all mb-4 shadow-sm mr-1 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-gradient-to-r after:from-white-500 after:via-[${slaColor.color}]-500 after:to-[${slaColor.color}]-500`}
        onClick={() => {
          onClick(data);
        }}
      >
        <div className="absolute -bottom-[7px] right-[18px] ">
          <div className="">
            {(data?.deliverAt != null || data?.deliveryTimeEnd != null || data?.deliveryTimeStart != null) ? (
              <span className="text-[10px] font-bold px-[4px] py-[2px] text-white rounded shadow-md mr-1 bg-[#0098b3ff]">
                Scheduled
              </span>
            ) : null}
            <span className="text-[10px] font-bold px-[4px] py-[2px] text-white rounded shadow-md" style={{ backgroundColor: slaColor.color }}>
              {sla}
            </span>
            {isBarq && promisedAtMs && !isCompletedOrCancelled ? (
              <span
                className="text-[10px] font-bold px-[4px] py-[2px] text-white rounded shadow-md ml-1"
                style={{ backgroundColor: isOverdue ? "#ea5034" : "#10b981" }}
                title={shipmentData?.promisedTime ?? data?.promisedTime ?? ""}
              >
                {timeLeftText}
              </span>
            ) : null}
          </div>
        </div>
        <div className={`relative my-2 px-4 py-2 capitalize flex items-center justify-between flex-col space-y-2`}>
          <div className="w-full flex justify-between">
            <div className="flex items-center justify-center space-x-1">
              {data?.number && <span className="text-black block bg-white rounded-full h-4 w-4 text-center">{data?.number}</span>}
              {data?.orderStatus == "new_order" ? (
                <span className="relative inline-flex items-center">
                  <span className={`absolute top-0 right-0 -mr-1 h-full w-[70%] rounded ${backgroundColors[statusColor]} opacity-99 animate-ping`}></span>
                  <span className={`relative z-10 px-2 py-0.5 rounded-md text-white capitalize ${backgroundColors[statusColor]}`}>
                    {data?.shipment?.id && <span className="rounded bg-gray-600 shadow-lg px-[4px] py-[2px] text-[9px] ml-[-3px] mr-[2px]">M</span>}
                    {data?.tracking_no} / {statusText}
                  </span>
                </span>
              ) : (
                <span className={`px-2 py-0.5 rounded-md text-white capitalize ${backgroundColors[statusColor]}`}>
                  {data?.shipment?.id && <span className="rounded bg-gray-600 shadow-lg px-[4px] py-[2px] text-[9px] ml-[-3px] mr-[2px]">M</span>} {data?.tracking_no} / {statusText}
                </span>
              )}
              <div>
                <span className="text-lg  flex items-center justify-center space-x-1" style={{ color: data?.color }}>
                  <span className="[text-shadow:_0_2px_4px_rgb(99_102_241_/_0.8)]" style={{ fontSize: "14px" }}>
                    {" "}
                    &#9864;
                  </span>
                </span>
              </div>
            </div>
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (!isOpen) {
                  getShipment({
                    variables: {
                      id: parseInt(data?.id),
                    },
                  });
                }
                setIsOpen(!isOpen);
              }}
              className="text-primaryColor font-medium hover:underline"
            >
              {isOpen ? <EyeSlashIcon /> : <EyeIcon />}
            </span>
          </div>
          <div className="flex flex-row items-center justify-start text-left w-full space-x-2">
            <div className="flex items-center space-x-2 bg-[#e6e6e6] rounded shadow-sm">
              <span className="text-[9px] font-bold px-[6px] py-[1px] text-[gray] mr-1">
                Created {data?.createdAt ? new Date(data.createdAt).toLocaleString() : "Not Available"}
              </span>
            </div>

            <div className="flex items-center space-x-2 bg-[#cde7dbd2] rounded shadow-sm">
              <span className="text-[9px] font-bold px-[6px] py-[1px] text-[gray] mr-1">
               Updated {data?.updatedAt ? new Date(data.updatedAt).toLocaleString() : "Not Available"}
              </span>
            </div>
          </div>

          <div className="flex flex-row items-end justify-between text-left w-full space-x-1">
            <div className="flex flex-col w-1/3">
              {isOpen ? (
                <a
                  className="hover:underline"
                  target="_blank"
                  href={`${process.env.REACT_APP_MODE === "production" && isOpen ? "https://barqfleet.com/admin/orders/" + shipmentData?.id : "https://staging.barqfleet.com/admin/orders/" + shipmentData?.id}`}
                >
                  <span className="text-primaryColor">{shipmentData?.id || ""}</span>
                </a>
              ) : (
                <span className="text-primaryColor">{shipmentData?.id || ""}</span>
              )}
              <span className="text-lightColor">Order ID</span>
            </div>
            <>
              <div className="flex flex-col w-1/3">
                <span>{shipmentData?.merchant?.name || "-"}</span>
                <span className="text-lightColor">Merchant</span>
              </div>
              <div className="flex flex-col w-1/3">
                <span className="">{shipmentData?.neighborhood || "-"}</span>
                <span className="text-lightColor">Neighborhood</span>
              </div>
            </>
          </div>

          {isOpen && !loading && (
            <>
              <div className="flex flex-row items-end justify-between text-left w-full space-x-1">
                <div className="flex flex-col w-1/3">
                  {isOpen && shipmentData?.courier?.id ? (
                    <a
                      className="hover:underline text-primaryColor"
                      target="_blank"
                      href={`${process.env.REACT_APP_MODE === "production" ? "https://barqfleet.com/admin/couriers/" + shipmentData?.courier?.id : "https://staging.barqfleet.com/admin/couriers/" + shipmentData?.courier?.id
                        }`}
                    >
                      <span>
                        {shipmentData?.courier?.avatarUrl && <img className="h-4 w-4 rounded-full border border-transparent bg-white" src={shipmentData?.courier?.avatarUrl} />}
                        {shipmentData?.courier?.firstName || ""} {shipmentData?.courier?.lastName || "-"}
                      </span>
                    </a>
                  ) : (
                    <span>
                      {shipmentData?.courier?.avatarUrl && <img className="h-4 w-4 rounded-full border border-transparent bg-white" src={shipmentData?.courier?.avatarUrl} />}
                      {shipmentData?.courier?.firstName || ""} {shipmentData?.courier?.lastName || "-"}
                    </span>
                  )}
                  <span className="text-lightColor">{t("assigned_knight")}</span>
                </div>
                <div className="flex flex-col w-1/3">
                  <span className={textColors[shipmentData?.hub?.isActive && shipmentData?.hub?.isOpen ? "success" : "danger"]}>{shipmentData?.hub?.isActive && shipmentData?.hub?.isOpen ? t("open") : t("closed")}</span>
                  <span className="text-lightColor">{t("hub_status")}</span>
                </div>
                <div className="flex flex-col w-1/3">
                  <span>{shipmentData?.payment_type?.split("_")?.join(" ")}</span>
                  <span className="text-lightColor">{t("payment_method")}</span>
                </div>
              </div>
              <div className="flex flex-row items-end justify-between text-left w-full space-x-1">
                <div className="flex flex-col w-1/3">
                  <span className="">{shipmentData?.customerDetails?.mobile}</span>
                  <span className="text-lightColor">Customer Phone</span>
                </div>
                <div className="flex flex-col w-1/3">
                  <a
                    className="hover:underline"
                    target="_blank"
                    href={`${process.env.REACT_APP_MODE === "production" && isOpen ? "https://barqfleet.com/admin/hubs/" + shipmentData?.hub?.id : "https://staging.barqfleet.com/admin/hubs/" + shipmentData?.hub?.id}`}
                  >
                    {shipmentData?.hub?.code && (
                      <div className="relative group">
                        <span
                          className="text-primaryColor inline-block max-w-[102px] leading-[1.2] hover:underline overflow-hidden"
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: "1.2",
                          }}
                        >
                          {shipmentData.hub.code}
                        </span>
                        <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-[#646464] text-[#e2e2e2] text-[10px] rounded px-[5px] py-[1px] z-10 whitespace-nowrap">
                          {shipmentData.hub.code}
                        </div>
                      </div>
                    )}
                  </a>
                  <span className="text-lightColor">{t("hub_code")}</span>
                </div>
                <div className="flex flex-col w-1/3">
                  <span className={textColors[color]}>{shipmentData?.invoiceTotal}</span>
                  <span className="text-lightColor">Invoice total</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
};

export default React.memo(OrderCard);
